import { forwardRef } from 'react';
import { cn } from '../../../lib/cn';
import { ChevronGlyph } from './icons';

/**
 * MenuList.
 *
 * CSS selector: `organisms.css .ds-menu`.
 *
 * Doctrine: a real `<ul>`, because a settings hub is a list and screen readers
 * should be told how many items it has. The CSS drops the last row's hairline
 * through `.ds-menu li:last-child .ds-menu__row`, which is why `MenuRow`
 * renders itself wrapped in an `<li>` by default. Break that wrapping and the
 * list grows a trailing rule.
 */
export const MenuList = forwardRef(function MenuList({ className, children, ...rest }, ref) {
  return (
    <ul ref={ref} className={cn('ds-menu', className)} {...rest}>
      {children}
    </ul>
  );
});

/**
 * MenuRow.
 *
 * CSS selector: `organisms.css .ds-menu__row`, `--danger`, `.ds-menu__icon`,
 * `.ds-menu__label`.
 *
 * Doctrine:
 *
 * - Generous 60px rows. Density is not the goal on a settings hub.
 * - Icons are MUTED LINE ICONS, never coloured tiles, at 1.8 stroke on a 24
 *   grid. The chevron opposite them is 2.25.
 * - `danger` recolours the row and its icon to `--error`. That is the only
 *   place colour is used as emphasis in this block, and it marks destruction,
 *   not importance.
 */
export const MenuRow = forwardRef(function MenuRow(
  {
    className,
    icon,
    label,
    trailing,
    danger = false,
    chevron = true,
    asListItem = true,
    ...rest
  },
  ref,
) {
  /* A ROW WITH NO `onClick` IS NOT A BUTTON, and this was a real bug rather
     than a purity point. Every row rendered as a `<button>`, including the
     ones whose whole job is to CARRY a control — the dark mode row holds a
     `Switch`, which is itself a button. A button inside a button is invalid,
     React said so in the console on every profile render, and the browser's
     recovery is to reparent the inner one: the switch ended up outside the
     row's event target, so tapping it toggled nothing. That is why dark mode
     on the profile did not work.

     So: interactive rows stay buttons, carrier rows are plain elements. The
     control inside them is the tab stop, which is what it should always have
     been. */
  const interactive = typeof rest.onClick === 'function';
  const Tag = interactive ? 'button' : 'div';

  const row = (
    <Tag
      ref={ref}
      {...(interactive ? { type: 'button' } : {})}
      className={cn('ds-menu__row', danger && 'ds-menu__row--danger', className)}
      {...rest}
    >
      {icon && <span className="ds-menu__icon">{icon}</span>}
      <span className="ds-menu__label">{label}</span>
      {trailing}
      {/* A chevron on a row you cannot press promises a destination that is
          not there, so it is suppressed rather than left to the caller. */}
      {chevron && interactive && <ChevronGlyph direction="right" />}
    </Tag>
  );

  return asListItem ? <li>{row}</li> : row;
});

export default MenuList;
